import LockIcon from '@mui/icons-material/Lock';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { getRouteIntentById } from '@finance/contracts';
import type { NavigationAvailability, FeatureAvailability, RouteIntentId } from '@finance/contracts';
import type { NavigationCardDescriptor } from '@finance/contracts';
import { getRouteById } from '../../../routes/registry';
import { isWipFeaturesVisible } from '../../../config/feature-flags';

/**
 * Normal navigation uses the product/brand accent only. Financial semantic
 * colors (success/warning/error) are reserved for actual state such as P&L,
 * stale data, control breaks, failed validation, or destructive actions.
 */
export const CARD_PALETTE = ['primary'] as const;
export type PaletteColor = (typeof CARD_PALETTE)[number];

export function accentColorFor(_card: NavigationCardDescriptor): PaletteColor {
  return 'primary';
}

export function buildCardTargetPath(routePath: string, routeIntentId?: RouteIntentId): string {
  if (!routeIntentId) return routePath;
  const intent = getRouteIntentById(routeIntentId);
  if (!intent?.availableFromHub) return routePath;

  const params = new URLSearchParams();
  params.set('intent', routeIntentId);
  return `${routePath}${routePath.includes('?') ? '&' : '?'}${params.toString()}`;
}

interface AvailabilityPresentation {
  labelKey?: string;
  icon: typeof LockIcon;
  muted: boolean;
}

export function getAvailabilityPresentation(
  availability: NavigationAvailability | FeatureAvailability,
): AvailabilityPresentation {
  switch (availability) {
    case 'PLANNED':
      return { labelKey: 'common:planned', icon: ScheduleIcon, muted: true };
    case 'LOCKED_PERMISSION':
    case 'LOCKED_TIER':
      return { labelKey: 'common:locked', icon: LockIcon, muted: true };
    case 'MAINTENANCE':
      return { labelKey: 'common:maintenance', icon: BuildCircleOutlinedIcon, muted: true };
    case 'FEATURE_DISABLED':
    case 'DATA_UNAVAILABLE':
      return { labelKey: 'common:unavailable', icon: BlockOutlinedIcon, muted: true };
    default:
      return { icon: LockIcon, muted: false };
  }
}

export function isCardRenderable(card: NavigationCardDescriptor): boolean {
  return card.availability !== 'HIDDEN' && (!card.wip || isWipFeaturesVisible());
}

export function isCardNavigable(card: NavigationCardDescriptor): boolean {
  if (card.availability !== 'AVAILABLE' && card.availability !== 'READ_ONLY') return false;
  return Boolean(getRouteById(card.routeId)?.path);
}
